import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { trackDoctorExport } from '../../../hooks/useGoogleAnalytics';

const ExportControls = ({ onExport, onShare, onEncryptedExport, summaryData, dateRange }) => {
  const { user, userProfile } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);

  const handlePDFExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    onExport('pdf');
    trackDoctorExport('pdf');
    setIsExporting(false);
  };

  const handlePrintExport = () => {
    window.print();
    onExport('print');
    trackDoctorExport('print');
  };

  const handleWhatsAppShare = () => {
    const startDate = new Date(dateRange?.start)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const endDate = new Date(dateRange?.end)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const rawEntries = summaryData?.rawEntries || [];
    
    let reportText = `*📋 HEALTH SUMMARY REPORT*\n`;
    reportText += `*MyBodyToday*\n\n`;
    reportText += `*Report Period:* ${startDate} - ${endDate}\n`;
    reportText += `*Total Check-ins:* ${rawEntries?.length}\n`;
    reportText += `${'='?.repeat(40)}\n\n`;
    
    // Detailed per-entry section
    if (rawEntries?.length > 0) {
      reportText += `*📅 DETAILED CHECK-IN ENTRIES*\n`;
      reportText += `${'-'?.repeat(40)}\n`;
      rawEntries?.forEach((entry, index) => {
        const entryDate = new Date(entry?.date)?.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        reportText += `\n*Entry ${index + 1} — ${entryDate}*\n`;
        reportText += `Overall Feeling: ${entry?.feelingLevel === 'good' ? '😊 Good' : entry?.feelingLevel === 'okay' ? '😐 Okay' : '😟 Unwell'}\n`;
        if (entry?.severityNumeric) reportText += `Severity: ${entry?.severityNumeric}/10\n`;
        if (entry?.symptomCategories?.length > 0) {
          reportText += `Symptoms: ${entry?.symptomCategories?.join(', ')}\n`;
        }
        if (entry?.otherSymptomText) reportText += `Additional symptom detail: ${entry?.otherSymptomText}\n`;
        if (entry?.bodyLocations?.length > 0) {
          reportText += `Body locations: ${entry?.bodyLocations?.join(', ')}\n`;
        }
        if (entry?.otherLocationText) reportText += `Exact location: ${entry?.otherLocationText}\n`;
        if (entry?.timing) reportText += `Timing: ${entry?.timing}\n`;
        if (entry?.triggers?.length > 0) reportText += `Triggers: ${entry?.triggers?.join(', ')}\n`;
        if (entry?.customTrigger) reportText += `Custom trigger: ${entry?.customTrigger}\n`;
        if (entry?.dailyImpacts?.length > 0) reportText += `Daily impacts: ${entry?.dailyImpacts?.join(', ')}\n`;
        if (entry?.medicationStatus) reportText += `Medication: ${entry?.medicationStatus}\n`;
        if (entry?.medicationDetails) reportText += `Medication details: ${entry?.medicationDetails}\n`;
        if (entry?.notes) reportText += `*Notes:* ${entry?.notes}\n`;
        if (entry?.photos?.length > 0) reportText += `📷 ${entry?.photos?.length} photo(s) attached (view in app)\n`;
      });
      reportText += `\n`;
    }
    
    // Symptoms Overview Section
    reportText += `*📊 SYMPTOMS OVERVIEW*\n`;
    reportText += `${'-'?.repeat(40)}\n`;
    if (summaryData?.symptoms && summaryData?.symptoms?.length > 0) {
      summaryData?.symptoms?.forEach((s, index) => {
        reportText += `${index + 1}. *${s?.name}*\n`;
        reportText += `   Location: ${s?.location}\n`;
        reportText += `   Avg Severity: ${s?.avgSeverity}/10\n`;
        reportText += `   Frequency: ${s?.frequency}x\n`;
        reportText += `   Duration: ${s?.duration}\n\n`;
      });
    } else {
      reportText += `No symptoms recorded\n\n`;
    }
    
    // Identified Triggers Section
    reportText += `*⚠️ IDENTIFIED TRIGGERS*\n`;
    reportText += `${'-'?.repeat(40)}\n`;
    if (summaryData?.triggers && summaryData?.triggers?.length > 0) {
      summaryData?.triggers?.forEach(trigger => {
        reportText += `• ${trigger}\n`;
      });
      reportText += `\n`;
    } else {
      reportText += `No triggers identified\n\n`;
    }
    
    // Daily Life Impact Section
    reportText += `*💼 DAILY LIFE IMPACT*\n`;
    reportText += `${'-'?.repeat(40)}\n`;
    if (summaryData?.impacts && summaryData?.impacts?.length > 0) {
      summaryData?.impacts?.forEach(i => {
        reportText += `• *${i?.category}:* ${i?.score}/10\n`;
      });
      reportText += `\n`;
    } else {
      reportText += `No impact data recorded\n\n`;
    }
    
    // Medication Adherence Section
    reportText += `*💊 MEDICATION ADHERENCE*\n`;
    reportText += `${'-'?.repeat(40)}\n`;
    if (summaryData?.medications && summaryData?.medications?.length > 0) {
      summaryData?.medications?.forEach(m => {
        reportText += `• ${m?.status}: ${m?.count} days\n`;
      });
      reportText += `\n`;
    } else {
      reportText += `No medication data recorded\n\n`;
    }
    
    // Footer
    reportText += `${'='?.repeat(40)}\n`;
    reportText += `\n_This report was generated by MyBodyToday_\n`;
    reportText += `_This information is confidential and contains symptom tracking data only._`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(reportText)}`;
    window.open(whatsappUrl, '_blank');
    onShare('whatsapp');
    setShowShareOptions(false);
  };

  const handleEmailShare = () => {
    setShowShareOptions(false);
    setShowEmailDialog(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSendEmail = async () => {
    setEmailError('');
    setEmailSuccess(false);

    if (!recipientEmail) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!validateEmail(recipientEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSendingEmail(true);

    try {
      // Prepare user information for the email
      const firstName = userProfile?.first_name || '';
      const lastName = userProfile?.last_name || '';
      const fullName = `${firstName} ${lastName}`?.trim() || 'Not provided';
      const userEmail = user?.email || userProfile?.email || 'Not provided';
      const userType = userProfile?.user_type || 'Not specified';

      const userInfo = {
        fullName,
        email: userEmail,
        userType
      };

      const { data, error } = await supabase?.functions?.invoke('send-doctor-summary', {
        body: {
          recipientEmail,
          summaryData,
          dateRange,
          userInfo
        }
      });

      if (error) {
        console.error('Supabase function invocation error:', error);
        
        // Handle specific error types from Supabase client
        if (error?.message?.includes('Failed to connect')) {
          throw new Error('Unable to connect to email service. Please check your internet connection.');
        } else if (error?.message?.includes('timeout')) {
          throw new Error('Request timed out. Please try again.');
        } else {
          throw new Error(error?.message || 'Failed to connect to email service');
        }
      }

      // Check if the function returned an error response
      if (!data?.success) {
        const errorMsg = data?.error || 'Failed to send email';
        const errorDetails = data?.details || '';
        console.error('Edge function returned error:', data);
        
        // If we have detailed error information, include it
        if (errorDetails) {
          throw new Error(`${errorMsg}\n\nDetails: ${errorDetails}`);
        }
        throw new Error(errorMsg);
      }

      // Success!
      setEmailSuccess(true);
      setRecipientEmail('');
      trackDoctorExport('email');
      
      setTimeout(() => {
        setShowEmailDialog(false);
        setEmailSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Email send error:', error);
      
      // Display specific error messages based on error content
      let errorMessage = 'Failed to send email. Please try again.';
      
      const errorStr = error?.message?.toLowerCase() || '';
      
      if (errorStr?.includes('not configured') || errorStr?.includes('service unavailable')) {
        errorMessage = '⚠️ Email service not configured.\n\nThe RESEND_API_KEY needs to be set in Supabase Dashboard:\n1. Go to Edge Functions → Secrets\n2. Add RESEND_API_KEY with your actual Resend key (starts with re_)\n3. Get your key from https://resend.com/api-keys';
      } else if (errorStr?.includes('authentication failed') || errorStr?.includes('api key')) {
        errorMessage = '⚠️ Email service authentication failed.\n\nPlease verify your RESEND_API_KEY in Supabase Dashboard:\n• The key should start with "re_"\n• Check it\'s correctly copied from Resend dashboard\n• Ensure no extra spaces or characters';
      } else if (errorStr?.includes('invalid') && errorStr?.includes('format')) {
        errorMessage = '⚠️ Invalid API key format.\n\nResend API keys must start with "re_".\nPlease check your RESEND_API_KEY in Supabase Edge Function secrets.';
      } else if (errorStr?.includes('unauthorized')) {
        errorMessage = 'Authentication failed. Please sign in again.';
      } else if (errorStr?.includes('too many requests') || errorStr?.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (errorStr?.includes('invalid email') || errorStr?.includes('422')) {
        errorMessage = 'Invalid email address format. Please check and try again.';
      } else if (errorStr?.includes('network') || errorStr?.includes('connect')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error?.message) {
        // Show the full error message including details
        errorMessage = error?.message;
      }
      
      setEmailError(errorMessage);
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic p-4 md:p-6">
      <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4 md:mb-6">
        Export Options
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        <Button
          variant="outline"
          size="lg"
          fullWidth
          iconName="Printer"
          iconPosition="left"
          onClick={handlePrintExport}
          className="min-h-[48px]"
        >
          Print Summary
        </Button>

        <Button
          variant="secondary"
          size="lg"
          fullWidth
          loading={isSharing}
          iconName="Share2"
          iconPosition="left"
          onClick={() => setShowShareOptions(!showShareOptions)}
          className="min-h-[48px]"
        >
          Share
        </Button>
      </div>
      <div className="mt-3 md:mt-4">
        <Button
          variant="default"
          size="lg"
          fullWidth
          iconName="Mail"
          iconPosition="left"
          onClick={() => setShowEmailDialog(true)}
          className="min-h-[48px] bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
        >
          Email Summary
        </Button>
      </div>
      {showEmailDialog && (
        <div className="mt-4 md:mt-6 p-4 md:p-6 bg-muted/50 rounded-lg animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-start gap-3 mb-4">
            <Icon name="Mail" size={20} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm md:text-base font-medium text-foreground mb-1">
                Send via Email
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Enter the recipient's email address to send this health summary report.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Input
              type="email"
              placeholder="doctor@example.com"
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e?.target?.value);
                setEmailError('');
              }}
              disabled={isSendingEmail}
              className="w-full"
            />

            {emailError && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={16} color="var(--color-destructive)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <p className="text-xs md:text-sm text-destructive">{emailError}</p>
              </div>
            )}

            {emailSuccess && (
              <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <p className="text-xs md:text-sm text-success">Email sent successfully!</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="default"
                size="default"
                fullWidth
                loading={isSendingEmail}
                iconName="Send"
                iconPosition="left"
                onClick={handleSendEmail}
                className="min-h-[44px]"
              >
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </Button>

              <Button
                variant="outline"
                size="default"
                onClick={() => {
                  setShowEmailDialog(false);
                  setRecipientEmail('');
                  setEmailError('');
                  setEmailSuccess(false);
                }}
                disabled={isSendingEmail}
                className="min-h-[44px] px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {showShareOptions && (
        <div className="mt-4 md:mt-6 p-4 md:p-6 bg-muted/50 rounded-lg animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-start gap-3 mb-4">
            <Icon name="Share2" size={20} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm md:text-base font-medium text-foreground mb-1">
                Share Your Report
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Choose how you'd like to share your health summary.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="Mail"
              iconPosition="left"
              onClick={handleEmailShare}
              className="min-h-[44px]"
            >
              Share via Email
            </Button>

            <Button
              variant="outline"
              size="default"
              fullWidth
              iconName="MessageCircle"
              iconPosition="left"
              onClick={handleWhatsAppShare}
              className="min-h-[44px] bg-[#25D366] hover:bg-[#20BA5A] text-white border-[#25D366]"
            >
              Share via WhatsApp
            </Button>

            <button
              onClick={() => setShowShareOptions(false)}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-smooth"
            >
              Cancel
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={14} color="var(--color-muted-foreground)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Your health data is private and secure. WhatsApp sharing sends a comprehensive formatted report.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex items-start gap-2">
          <Icon name="Shield" size={16} color="var(--color-success)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-muted-foreground">
            All exports are privacy-focused with encrypted health data storage. Your symptom tracking data remains private and secure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExportControls;